import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, UploadSimple, X, CircleNotch, Package, Trash, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function Products() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [images, setImages] = useState([]); // {asset_id, url}
  const fileRef = useRef();

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files).slice(0, 5 - images.length);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", "reference");
        const { data } = await api.post("/assets/upload", fd);
        setImages((prev) => [...prev, data]);
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const create = async () => {
    if (!form.name) { toast.error(t("products.needName")); return; }
    setLoading(true);
    try {
      await api.post("/products", { ...form, reference_asset_ids: images.map((i) => i.asset_id) });
      toast.success(t("products.created"));
      setOpen(false); setForm({ name: "", description: "" }); setImages([]);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const remove = async (id, e) => {
    e.preventDefault();
    await api.delete(`/products/${id}`);
    toast.success(t("products.deleted")); load();
  };

  const duplicate = async (id, e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/products/${id}/duplicate`);
      toast.success(t("products.duplicated", { name: data.name }));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("products.title")}</h1>
          <p className="text-neutral-400 mt-1">{t("products.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-product-btn" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold"><Plus className="mr-1" /> {t("products.newProduct")}</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-neutral-200">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{t("products.newProduct")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t("products.name")}</Label>
                <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-white/5 border-white/15" placeholder={t("products.namePlaceholder")} />
              </div>
              <div>
                <Label>{t("products.description")}</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-white/5 border-white/15" placeholder={t("products.descPlaceholder")} />
              </div>
              <div>
                <Label>{t("products.refImages")}</Label>
                <p className="text-[11px] text-neutral-500 mt-1">{t("products.refImagesHint")}</p>
                <div className="mt-2 grid grid-cols-5 gap-2">
                  {images.map((img, i) => (
                    <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
                      <img src={assetUrl(img.url)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <button onClick={() => fileRef.current?.click()} data-testid="upload-ref-btn" className="aspect-square rounded-lg border border-dashed border-white/25 flex items-center justify-center hover:border-gold text-neutral-400 hover:text-gold">
                      {uploading ? <CircleNotch className="animate-spin" /> : <UploadSimple size={20} />}
                    </button>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
              </div>
              <Button data-testid="create-product-submit" onClick={create} disabled={loading} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold">
                {loading ? <CircleNotch className="animate-spin" /> : t("products.createProduct")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {products.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center">
          <Package size={40} className="text-neutral-600 mx-auto mb-4" weight="duotone" />
          <p className="text-neutral-400">{t("products.empty")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
          {products.map((p) => (
            <Link key={p.id} to={`/dashboard/products/${p.id}`} data-testid={`product-card-${p.id}`}
              className="group border border-white/10 rounded-xl overflow-hidden bg-[#121212] hover:border-white/25 transition-colors">
              <div className="aspect-video bg-neutral-900 relative overflow-hidden">
                {p.base_asset_id ? <img src={assetUrl(`/api/assets/${p.base_asset_id}`)} alt="" className="w-full h-full object-cover" /> : null}
                <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`duplicate-product-${p.id}`} onClick={(e) => duplicate(p.id, e)} title={t("products.duplicateTitle")} className="bg-black/70 hover:bg-gold hover:text-obsidian rounded-full p-1.5"><Copy size={14} /></button>
                  <button data-testid={`delete-product-${p.id}`} onClick={(e) => remove(p.id, e)} title={t("products.deleteTitle")} className="bg-black/70 hover:bg-red-500 rounded-full p-1.5"><Trash size={14} /></button>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${p.status === "draft" ? "bg-white/10 text-neutral-400" : "bg-gold/20 text-gold"}`}>{p.status}</span>
                  {p.product_type_label && <span className="text-xs text-neutral-500 truncate">{p.product_type_label}</span>}
                </div>
                <h3 className="font-semibold mt-2 truncate">{p.name}</h3>
                {(p.updated_by || p.created_by) && <p className="text-[11px] text-neutral-500 mt-1 truncate" data-testid={`product-editor-${p.id}`}>Editado por {p.updated_by || p.created_by}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
